import 'react-toastify/dist/ReactToastify.css'
import './assets/custom.css'
import './assets/main.css'

import { useAuthContext } from 'auth/authContext'
import GlobalLoading from 'components/GlobalLoading'
import Loading from 'components/Loading'
import Alert from 'components/Modal/Alert'
import NavBar from 'components/NavBar'
import OfflineBanner from 'components/OfflineBanner'
import PrivateRoute from 'components/PrivateRoute'
import { SubscriptionAlertHandler } from 'components/SubscriptionAlertHandler'
import useCheckVersion from 'hooks/useCheckVersion'
import omit from 'lodash/omit'
import TabletModeMain from 'pages/tabletMode/TabletModeMain'
import QuestionContext from 'questions/QuestionContext'
import * as React from 'react'
import { Redirect, Route, Router, Switch } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import FullHeightContainer from 'ui/atoms/FullHeightContainer'
import history from 'utils/history'
import { IntegrationsContext } from 'utils/IntegrationsContext'
import { ZentakeContext } from 'utils/ZentakeContext'

import MainRouter from './MainRouter'
import routes from './routes'
import cn from './utils/tailwind/cn'

function App(): React.Node {
  const { loading, isAuthenticated, authIsReady } = useAuthContext()
  const [ready, setReady] = React.useState(false)
  // eslint-disable-next-line no-unused-vars
  const _ = useCheckVersion()

  const { getStripeAccountStatus, globalLoading } =
    React.useContext(ZentakeContext)

  const { getGoogleIntegration } = React.useContext(IntegrationsContext)

  const isPatientForm = history?.location?.pathname?.includes('/f/')

  const formOfficeUse = history?.location?.pathname?.includes(
    'fillFormOfficeUseQuestions'
  )

  const packetOfficeUse = history?.location?.pathname?.includes(
    'fillPacketOfficeUseQuestions'
  )

  const officeMode = formOfficeUse || packetOfficeUse

  React.useEffect(() => {
    async function init() {
      await Promise.all([getGoogleIntegration(), getStripeAccountStatus()])

      setReady(true)
    }

    if (!loading && authIsReady && isAuthenticated) {
      init()
      return
    }

    if (!loading && authIsReady && !isAuthenticated) {
      setReady(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, loading, authIsReady])

  if (loading || !authIsReady || !ready) {
    return <Loading />
  }

  return (
    <>
      {globalLoading ? <GlobalLoading /> : null}
      <QuestionContext>
        <Router history={history}>
          <FullHeightContainer
            className={cn({
              'form-wrapper': isPatientForm,
              'overflow-hidden': officeMode,
            })}
          >
            <NavBar />
            <SubscriptionAlertHandler />
            <OfflineBanner />
            <Switch>
              {routes
                .filter((route) => !route.private)
                .map((route) => {
                  const Component = route.component

                  return (
                    <Route
                      component={Component}
                      exact={route.exact}
                      key={route.path}
                      path={route.path}
                      route={omit(route, 'component')}
                    />
                  )
                })}
              <PrivateRoute component={TabletModeMain} path="/t" />
              <Route path="/" render={(props) => <MainRouter {...props} />} />
              <Redirect to="/" />
            </Switch>
            <Alert isTablet={false} />
            <ToastContainer
              closeOnClick
              draggable
              hideProgressBar
              pauseOnFocusLoss
              pauseOnHover
              autoClose={4500}
              bodyClassName="font-sans text-base"
              position="top-right"
              rtl={false}
            />
          </FullHeightContainer>
        </Router>
      </QuestionContext>
    </>
  )
}

export default App
